'use client';
import { currentAccountAtom } from '@/app/states/atoms';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

/**
 * Protect component that gates access to content based on user roles.
 * Currently supports checking for system admin access via the currentUserAtom.
 *
 * @example
 * // Basic usage - content will not render for non-admins
 * <Protect roles={['SYSTEM:ADMIN']}>
 *   <AdminOnlyContent />
 * </Protect>
 *
 * @example
 * // With fallback content for non-admins
 * <Protect
 *   roles={['SYSTEM:ADMIN']}
 *   fallback={<p>You need admin access to view this content</p>}
 * >
 *   <div>
 *     <h2>Sensitive Information</h2>
 *     <AdminControls />
 *   </div>
 * </Protect>
 */
export interface ProtectProps {
  /** The role required to access the content. Currently only supports 'admin' */
  roles: ('SYSTEM:ADMIN' | 'ACCOUNT:ADMIN' | 'ACCOUNT:MEMBER' | 'AGENCY:USER')[];
  /** Optional content to display when access is denied. If not provided, renders null */
  fallback?: ReactNode;
  /** The content to render when access is granted */
  children: ReactNode;
}

export function Protect({ roles, fallback, children }: ProtectProps) {
  const findIntersectionFilterIncludes = (arr1, arr2) => {
    return arr1.filter(element => arr2.includes(element)).length > 0;
  };
  const currentAccount = useAtomValue(currentAccountAtom);
  const hasAccess = findIntersectionFilterIncludes(currentAccount?.roles || [], roles);

  if (hasAccess) {
    return <>{children}</>;
  }

  return fallback ? <>{fallback}</> : null;
}
